'use client';

import { useTheme } from '@emotion/react';
import { MouseEvent, useEffect, useRef, useState } from 'react';
import { Image, Picture } from '../picture/picture';
import { DEVICE_SCREENS, DEVICE_SCREENS_MAX_STRING } from '../../lib/consts';
import ArrowSVG from '../../public/img/angle-right.svg';
import { ImageProp } from '../../models/image';
import videoCoverImg from '../../public/img/slider-video-placeholder.png';

export function ImagesSlider({
  videoInfo,
  images,
  onSlideClick,
}: {
  videoInfo: {
    video: string;
    cover: string;
  };
  images: ImageProp[];
  onSlideClick: (index: number) => void;
}) {
  const slider = useRef<HTMLDivElement | null>(null);
  const items = useRef<HTMLDivElement | null>(null);
  const [showArrow, setShowArrow] = useState<boolean>(false);
  const theme = useTheme();
  let pressed = false;
  let clientX = 0;
  let scrollX = 0;
  let rightMax = 0;

  useEffect(() => {
    if (window.innerWidth <= DEVICE_SCREENS.sm) {
      setShowArrow(false);
    } else if (videoInfo.video && images.slice(1).length > 3) {
      setShowArrow(true);
    } else if (images.slice(1).length > 4) {
      setShowArrow(true);
    } else {
      setShowArrow(false);
    }
  }, [images, videoInfo]);

  const automaticSwitch = () => {
    if (slider?.current) {
      const coefficient = Math.round(Math.abs(scrollX / 180));
      slider.current.scrollTo({ left: 180 * coefficient, behavior: 'smooth' });
      scrollX = -(180 * coefficient);
    }
  };

  const onMouseDown = (e: MouseEvent) => {
    e.preventDefault();

    pressed = true;
    clientX = e.clientX;
  };

  const onMouseUp = (e: MouseEvent) => {
    e.preventDefault();

    if (items.current) {
      items.current.style.pointerEvents = 'unset';
    }
    pressed = false;
    automaticSwitch();
  };

  const onMouseMove = (e: MouseEvent) => {
    e.preventDefault();

    if (pressed && items.current && slider.current) {
      items.current.style.pointerEvents = 'none';

      let sX = scrollX + e.clientX - clientX;
      const cX = e.clientX;

      slider.current.scrollLeft = -sX;

      if (!rightMax) {
        rightMax = 180 * images.length - slider.current.clientWidth;
      }

      if (rightMax < -sX) {
        sX = -rightMax;
      } else if (-sX < 0) {
        sX = 0;
      }

      scrollX = sX;
      clientX = cX;
    }
  };

  const slideLeft = () => {
    if (!slider.current) return;
    if (!rightMax) {
      rightMax = 180 * images.length - slider.current.clientWidth;
    }
    if (scrollX < 0) {
      scrollX = -scrollX - 180 < 180 ? 0 : scrollX + 180;
      slider.current.scrollTo({ left: -scrollX, behavior: 'smooth' });
    }
  };

  const slideRight = () => {
    if (!slider.current) return;
    if (!rightMax) {
      rightMax = 180 * images.length - slider.current.clientWidth;
    }
    if (rightMax > -scrollX) {
      scrollX = rightMax + scrollX < 180 ? -rightMax : scrollX - 180;
      slider.current.scrollTo({ left: -scrollX, behavior: 'smooth' });
    }
  };

  const onResize = () => {
    if (slider.current) {
      rightMax = 180 * images.length - slider.current.clientWidth;
    }
  };

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div css={{ display: 'flex', alignItems: 'center' }}>
      {showArrow && (
        <button
          css={{
            position: 'absolute',
            left: '-20px',
            width: '3%',
          }}
          type='button'
          onClick={slideLeft}
        >
          <ArrowSVG
            css={{
              transform: 'rotate(180deg)',
              width: 10,
              height: 16,
              fill: theme.colors.primary,
            }}
          />
        </button>
      )}
      <div
        ref={slider}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        onMouseLeave={() => {
          pressed = false;
        }}
        css={{
          marginBottom: 20,
          width: '100%',
          overflowX: 'scroll',
          display: 'flex',
          gap: 30,
          scrollbarWidth: 'none',
          '-ms-overflow-style': 'none',
          '&::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <div
          ref={items}
          css={{
            display: 'flex',
            gap: 8,
          }}
        >
          {videoInfo.video && (
            <div
              css={{
                width: '24%',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                height: 177,
                minWidth: 175,
                cursor: 'pointer',
                [DEVICE_SCREENS_MAX_STRING.xxl]: {
                  minWidth: 157,
                  height: 157,
                },
                [DEVICE_SCREENS_MAX_STRING.xl]: {
                  height: 132,
                  minWidth: 132,
                },
                [DEVICE_SCREENS_MAX_STRING.lg]: {
                  height: 84,
                  minWidth: 84,
                },
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  height: 120,
                  minWidth: 160,
                },
              }}
              onClick={() => onSlideClick(0)}
            >
              <Image
                css={{
                  position: 'absolute',
                  inset: 0,
                  height: '100%',
                  width: '100%',
                  objectFit: 'cover',
                  zIndex: 1,
                }}
                image={videoInfo.cover}
              />
              <Image
                css={{
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  position: 'absolute',
                  height: '70%',
                  width: '70%',
                  objectFit: 'cover',
                  zIndex: 2,
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    height: '75%',
                  },
                }}
                image={videoCoverImg.src}
              />
            </div>
          )}
          {images.slice(1).map((image, index) => (
            <div
              key={image.id}
              css={{
                width: '24%',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                height: 177,
                minWidth: 175,
                cursor: 'pointer',
                [DEVICE_SCREENS_MAX_STRING.xxl]: {
                  minWidth: 157,
                  height: 157,
                },
                [DEVICE_SCREENS_MAX_STRING.xl]: {
                  height: 132,
                  minWidth: 132,
                },
                [DEVICE_SCREENS_MAX_STRING.lg]: {
                  height: 84,
                  minWidth: 84,
                },
                [DEVICE_SCREENS_MAX_STRING.md]: {
                  height: 120,
                  minWidth: 160,
                },
              }}
              onClick={() => onSlideClick(videoInfo.video ? index + 1 : index)}
            >
              <Picture alt='picture' src={image} />
            </div>
          ))}
        </div>
      </div>
      {showArrow && (
        <button
          css={{
            position: 'absolute',
            right: '-20px',
            width: '3%',
          }}
          type='button'
          onClick={slideRight}
        >
          <ArrowSVG
            css={{ width: 10, height: 16, fill: theme.colors.primary }}
          />
        </button>
      )}
    </div>
  );
}
